import * as Axios from 'axios';
import {useAuthStore, useValueDataStore} from '@/stores'
import {
    type DBGHubPayload,
    type Dataset,
    type ValueUser,
    EntityType,
    type GroupMetaValues,
    type GroupSummary, type HubRole, type Permissions,
    type StoreSummary
} from '@/data'
import {ensureValueAccessOrLogin} from "@/stores/refreshOrLogin";
import {VALUE_RESOURCE} from "@/stores/resources";
import {getDatasetId} from "@/dataset.ts";
import {DATASET_OVERRIDE_HEADER} from "@/const.ts";

export class ValueService {

    public async refreshStoreSummaryList(): Promise<StoreSummary[] | null> {
        if (await ensureValueAccessOrLogin()) {
            return [];
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/stores/summary`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);

            return res.data.stores as StoreSummary[];
        } catch (e: any) {
            console.error(`Error fetching stores summary: ${e.message}`);
            return [];
        }
    }

    public async refreshGroupSummaryList(): Promise<GroupSummary[] | null> {
        if (await ensureValueAccessOrLogin()) {
            return [];
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/groups/summary`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);

            return res.data.groups as GroupSummary[];
        } catch (e: any) {
            console.error(`Error fetching groups summary: ${e.message}`);
            return null;
        }
    }

    public async fetchRoles(): Promise<HubRole[] | null> {
        if (await ensureValueAccessOrLogin()) {
            return [];
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/roles`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data.roles as HubRole[];
        } catch (e: any) {
            console.error(`Error fetching roles summary: ${e.message}`);
            return null;
        }
    }

    public async loadValueData(type: EntityType, id: string): Promise<DBGHubPayload | null> {
        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/${type === EntityType.GROUP ? 'groups' : 'stores' }/${id}`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as DBGHubPayload;
        } catch (e: any) {
            console.error(`Error fetching value data: ${e.message}`);
            return null;
        }
    }

    public async fetchGroupMetadata(groupId: string): Promise<GroupMetaValues | null> {
        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        try {
            const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
            const {apiBase} = useValueDataStore();

            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/admin/groups/${groupId}/meta`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as GroupMetaValues;
        } catch (e: any) {
            console.error(`Error fetching group metadata: ${e.message}`);
            return null;
        }
    }

    public async saveGroupMetadata(groupId: string, values: GroupMetaValues): Promise<void> {

        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'post',
            url: `${apiBase}/admin/groups/${groupId}/meta`,
            headers: {
                'Authorization': `Bearer ${accessToken}`
            },
            data: values
        }

        await Axios.default(apiConfig);
    }

    public async fetchUserPermissions(userId: string): Promise<Permissions | null> {

        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/admin/users/${userId}/permissions`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as Permissions;
        } catch (e: any) {
            console.error(`Error fetching user permissions: ${e.message}`);
            return null;
        }
    }

    public async saveUserPermissions(userId: string, permissions: Permissions): Promise<void> {

        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        const apiConfig: Axios.AxiosRequestConfig = {
            method: 'post',
            url: `${apiBase}/admin/users/${userId}/permissions`,
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
            },
            data: permissions
        }

        await Axios.default(apiConfig);
    }

    public async uploadAssignmentsCsv(fileName: string, csvContent: string): Promise<void> {
        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: "post",
                url: `${apiBase}/admin/assignments`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                },
                data: {
                    fileName,
                    content: csvContent,
                },
            };

            await Axios.default(apiConfig);
        } catch (e: any) {
            throw new Error(e.response?.data?.message || e.message || 'Unhandled error during CSV upload');
        }
    }

    public async fetchDatasets(): Promise<Dataset[]> {
        if (await ensureValueAccessOrLogin()) {
            return [];
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/admin/datasets`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as Dataset[];
        } catch (e: any) {
            console.error(`Error fetching datasets: ${e.message}`);
            throw new Error(e.response?.data?.message || e.message || 'Failed to fetch datasets');
        }
    }

    public async fetchAdminConfig(): Promise<{ activeDatasetId?: string } | null> {
        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/admin/config`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data;
        } catch (e: any) {
            console.error(`Error fetching admin config: ${e.message}`);
            return null;
        }
    }

    public async activateDataset(datasetId: string): Promise<void> {
        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'put',
                url: `${apiBase}/admin/config`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                data: {
                    activeDatasetId: datasetId
                }
            }

            await Axios.default(apiConfig);
        } catch (e: any) {
            throw new Error(e.response?.data?.message || e.message || 'Failed to activate dataset');
        }
    }

    public async deleteDataset(datasetId: string): Promise<void> {
        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'delete',
                url: `${apiBase}/admin/datasets/${datasetId}`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }

            await Axios.default(apiConfig);
        } catch (e: any) {
            throw new Error(e.response?.data?.message || e.message || 'Failed to delete dataset');
        }
    }

    public async fetchCurrentUser(): Promise<ValueUser | null> {
        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/user`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as ValueUser;
        } catch (e: any) {
            console.error(`Error fetching current user: ${e.message}`);
            return null;
        }
    }

    public async createDataset(dataset: Partial<Dataset>): Promise<void> {
        if (await ensureValueAccessOrLogin()) {
            return;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'post',
                url: `${apiBase}/admin/datasets`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                },
                data: dataset
            }

            await Axios.default(apiConfig);
        } catch (e: any) {
            throw new Error(e.response?.data?.message || e.message || 'Failed to create dataset');
        }
    }

    public async fetchEffectiveDataset(): Promise<Dataset | null> {
        if (await ensureValueAccessOrLogin()) {
            return null;
        }

        const accessToken = useAuthStore().getAccessToken(VALUE_RESOURCE);
        const { apiBase } = useValueDataStore();

        const datasetId = getDatasetId();

        try {
            const apiConfig: Axios.AxiosRequestConfig = {
                method: 'get',
                url: `${apiBase}/value/dataset`,
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    ...(datasetId ? { [DATASET_OVERRIDE_HEADER]: datasetId } : {})
                }
            }

            const res: Axios.AxiosResponse = await Axios.default(apiConfig);
            return res.data as Dataset;
        } catch (e: any) {
            console.error(`Error fetching effective dataset: ${e.message}`);
            return null;
        }
    }


}
